import { useMemo, useState } from "react";
import axios from "axios";
import {
  MapPin,
  Phone,
  Clock,
  EnvelopeSimple,
  ArrowRight,
  CaretDown,
  UserCircle,
  WhatsappLogo,
  Sparkle,
  CheckCircle,
  WarningCircle,
} from "@phosphor-icons/react";
import { AsYouType, isValidPhoneNumber } from "libphonenumber-js";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";

import { COUNTRIES, DEFAULT_DIAL } from "../lib/countries";
import { useContactQueryPrefill } from "../lib/useContactQueryPrefill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  "w-full rounded-lg border border-slate-400 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20";

const MAP_EMBED =
  "https://www.google.com/maps?q=Rua%20Padre%20Manuel%20Ruela%20Pombo%2040%2C%20Maianga%2C%20Luanda%2C%20Angola&output=embed";

const InfoRow = ({ Icon, label, children, testId }) => (
  <div
    data-testid={testId}
    className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-blue-300"
  >
    <div className="icon-tile shrink-0">
      <Icon size={20} weight="duotone" />
    </div>
    <div>
      {label ? (
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-600">
          {label}
        </div>
      ) : null}
      <div className={`${label ? "mt-1.5" : ""} text-sm leading-relaxed text-slate-800`}>
        {children}
      </div>
    </div>
  </div>
);

export const Contact = () => {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    dial: DEFAULT_DIAL,
    phone: "",
    subject: "",
    message: "",
  });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  // Resolve the ISO-2 code of the currently selected dial code so the
  // libphonenumber `AsYouType` formatter knows which national format to use.
  const currentIso = useMemo(
    () => COUNTRIES.find((c) => c.dial === form.dial)?.iso || "AO",
    [form.dial],
  );

  // Auto-format the phone number as the user types (e.g. "923456789" → "923 456 789").
  const onPhoneChange = (e) => {
    const formatted = new AsYouType(currentIso).input(e.target.value);
    setForm((f) => ({ ...f, phone: formatted }));
  };

  // Validity is only computed once the user has typed something —
  // an empty field stays neutral (phone is optional).
  const phoneValidity = useMemo(() => {
    if (!form.phone.trim()) return "empty";
    try {
      return isValidPhoneNumber(`+${form.dial}${form.phone.replace(/\D/g, "")}`)
        ? "valid"
        : "invalid";
    } catch {
      return "invalid";
    }
  }, [form.phone, form.dial]);

  // Prefill subject/message when navigating from a "Pedir orçamento" button
  // via ?produto=<name>&marca=<brand>
  useContactQueryPrefill(setForm, lang);

  // When the user switches country, re-format the existing phone using the
  // new country's national format so it stays consistent.
  const onDialChange = (e) => {
    const newDial = e.target.value;
    const newIso = COUNTRIES.find((c) => c.dial === newDial)?.iso || "AO";
    setForm((f) => ({
      ...f,
      dial: newDial,
      phone: f.phone ? new AsYouType(newIso).input(f.phone.replace(/\D/g, "")) : "",
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.form.required);
      return;
    }
    // Block submission when a phone is entered but doesn't pass E.164 validation.
    if (phoneValidity === "invalid") {
      toast.error(t.contact.form.phone_invalid);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone ? `+${form.dial} ${form.phone}`.trim() : "",
        subject: form.subject,
        message: form.message,
        locale: lang,
      };
      await axios.post(`${API}/contact`, payload);
      toast.success(t.contact.form.success);
      setForm({
        name: "",
        email: "",
        dial: DEFAULT_DIAL,
        phone: "",
        subject: "",
        message: "",
      });
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative scroll-mt-[160px] md:scroll-mt-[210px] lg:scroll-mt-[240px] border-t border-slate-200 py-24 lg:py-32"
    >
      {/* Background photo: communication channels */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <img
          src="/sections/contact-bg.png"
          alt=""
          aria-hidden
          loading="lazy"
          className="h-full w-full object-cover object-center"
          style={{ filter: "saturate(1.15) contrast(1.05)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#a8c8e2]/25 via-[#bcd5e8]/30 to-[#d6e6f2]/45" />
      </div>
      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="mt-2 space-y-4">
              <InfoRow
                Icon={MapPin}
                label={t.contact.address_label}
                testId="contact-address"
              >
                {t.contact.address_value}
              </InfoRow>
              <InfoRow
                Icon={Clock}
                label={t.contact.hours_label}
                testId="contact-hours"
              >
                {t.contact.hours_value}
              </InfoRow>
            </div>

            {/* Commercial Contacts card */}
            {[
              {
                key: "area_comercial",
                title: t.contact.area_comercial_title,
                list: t.contact.area_comercial_list,
              },
              {
                key: "support",
                title: t.contact.support_title,
                list: t.contact.support_list,
              },
              {
                key: "commercial",
                title: t.contact.commercial_title,
                list: t.contact.commercial_list,
              },
              {
                key: "finance",
                title: t.contact.finance_title,
                list: t.contact.finance_list,
              },
              {
                key: "direction",
                title: t.contact.direction_title,
                list: t.contact.direction_list,
              },
            ].map((block) => {
              // Compact layout for the support card: phones inline per row,
              // email displayed once at the bottom (de-duplicated).
              if (block.key === "support") {
                const sharedEmails = Array.from(
                  new Set(block.list.flatMap((c) => c.emails || [])),
                );
                return (
                  <div
                    key={block.key}
                    data-testid={`contact-${block.key}`}
                    className="mt-6 overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-[#d8e8f5] to-sky-50 p-5 shadow-sm"
                  >
                    <div className="flex items-center gap-2">
                      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white">
                        <UserCircle size={18} weight="fill" />
                      </div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-700">
                        {block.title}
                      </div>
                    </div>

                    <div className="mt-4 space-y-2">
                      {block.list.map((c) => {
                        const phones = c.phones || [
                          { label: c.phone, href: c.phone_href },
                        ];
                        return (
                          <div
                            key={c.name}
                            data-testid={`${block.key}-card-${c.name}`}
                            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-blue-100 bg-white px-4 py-2.5 text-sm"
                          >
                            <span className="font-heading text-sm font-bold text-slate-900">
                              {c.name}:
                            </span>
                            <Phone
                              size={13}
                              weight="bold"
                              className="text-blue-600"
                            />
                            {phones.map((p, idx) => (
                              <span key={p.href} className="flex items-center">
                                {idx > 0 && (
                                  <span className="mx-1.5 text-slate-300">·</span>
                                )}
                                <a
                                  href={`tel:${p.href}`}
                                  className="font-semibold text-slate-900 transition-colors hover:text-blue-700"
                                >
                                  {p.label}
                                </a>
                              </span>
                            ))}
                          </div>
                        );
                      })}
                      {sharedEmails.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-blue-100 bg-white px-4 py-2.5 text-sm">
                          <EnvelopeSimple
                            size={13}
                            weight="bold"
                            className="text-blue-600"
                          />
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                            {t.contact.commercial_email_label}:
                          </span>
                          {sharedEmails.map((email, idx) => (
                            <span key={email} className="flex items-center">
                              {idx > 0 && (
                                <span className="mx-1 text-slate-300">·</span>
                              )}
                              <a
                                href={`mailto:${email}`}
                                className="font-semibold text-slate-900 transition-colors hover:text-blue-700"
                              >
                                {email}
                              </a>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }
              return (
              <div
                key={block.key}
                data-testid={`contact-${block.key}`}
                className="mt-6 overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-[#d8e8f5] to-sky-50 p-5 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white">
                    <UserCircle size={18} weight="fill" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-700">
                    {block.title}
                  </div>
                </div>

                <div
                  className={`mt-4 grid grid-cols-1 gap-3 ${
                    block.list.length > 1 ? "md:grid-cols-2" : ""
                  }`}
                >
                  {block.list.map((c) => (
                    <div
                      key={c.name}
                      data-testid={`${block.key}-card-${c.name}`}
                      className="rounded-xl border border-blue-100 bg-white p-4"
                    >
                      <div className="font-heading text-sm font-bold text-slate-900">
                        {c.name}
                      </div>
                      <div className="text-xs text-slate-600">{c.role}</div>
                      <div className="mt-3 space-y-1.5 text-sm text-slate-800">
                        {(c.phones || (c.phone ? [{ label: c.phone, href: c.phone_href }] : [])).map((p) => (
                          <div key={p.href} className="flex items-center gap-2">
                            <Phone size={13} weight="bold" className="text-blue-600" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                              {t.contact.commercial_phone_label}:
                            </span>
                            <a
                              href={`tel:${p.href}`}
                              className="font-semibold text-slate-900 transition-colors hover:text-blue-700"
                            >
                              {p.label}
                            </a>
                          </div>
                        ))}
                        {c.emails.map((email) => (
                          <div key={email} className="flex items-center gap-2">
                            <EnvelopeSimple
                              size={13}
                              weight="bold"
                              className="text-blue-600"
                            />
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                              {t.contact.commercial_email_label}:
                            </span>
                            <a
                              href={`mailto:${email}`}
                              className="truncate text-xs font-semibold text-slate-900 transition-colors hover:text-blue-700"
                            >
                              {email}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              );
            })}
          </div>

          <div className="lg:col-span-7">
            <div
              data-testid="contact-cta-banner"
              className="mb-6 overflow-hidden rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-700 p-6 text-white shadow-xl shadow-blue-600/25 md:p-8"
            >
              <div className="flex items-start gap-4">
                <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25 backdrop-blur-sm md:inline-flex">
                  <ArrowRight size={22} weight="bold" className="text-white" />
                </div>
                <div>
                  <div className="font-heading text-lg font-bold leading-snug md:text-xl">
                    {t.contact.cta_banner_title}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-white/85">
                    {t.contact.cta_banner_desc}
                  </p>
                </div>
              </div>
            </div>
            <div className="mb-6">
              <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-600">
                {t.contact.overline}
              </div>
              <h2
                data-testid="contact-title"
                className="font-heading mt-3 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl lg:text-4xl"
              >
                {t.contact.title}
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
                {t.contact.subtitle}
              </p>
            </div>
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="contact-form"
              className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-lg md:p-10"
            >
              <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-blue-400/15 blur-3xl" />

              <div className="relative grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {t.contact.form.name} *
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder={t.contact.form.name_ph}
                    className={inputBase}
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {t.contact.form.email} *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder={t.contact.form.email_ph}
                    className={inputBase}
                    data-testid="contact-input-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {t.contact.form.phone}
                  </label>
                  <div className="flex gap-2">
                    <div className="relative w-28 shrink-0">
                      <select
                        name="dial"
                        value={form.dial}
                        onChange={onDialChange}
                        data-testid="contact-input-dial"
                        className="h-full w-full appearance-none rounded-lg border border-slate-400 bg-white py-3 pl-3 pr-7 text-sm text-slate-900 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      >
                        {COUNTRIES.map((c) => (
                          <option key={c.iso} value={c.dial} label={`${c.flag} +${c.dial}`}>
                            {`${c.flag} +${c.dial}`}
                          </option>
                        ))}
                      </select>
                      <CaretDown
                        size={12}
                        weight="bold"
                        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                    </div>
                    <div className="relative flex-1">
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={onPhoneChange}
                        placeholder={t.contact.form.phone_ph}
                        className={`${inputBase} pr-10 ${
                          phoneValidity === "valid"
                            ? "border-emerald-500 focus:border-emerald-500 focus:ring-emerald-500/20"
                            : ""
                        } ${
                          phoneValidity === "invalid"
                            ? "border-rose-400 focus:border-rose-500 focus:ring-rose-500/20"
                            : ""
                        }`}
                        data-testid="contact-input-phone"
                        aria-invalid={phoneValidity === "invalid"}
                      />
                      {phoneValidity === "valid" && (
                        <CheckCircle
                          size={20}
                          weight="fill"
                          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500"
                          data-testid="phone-valid-icon"
                        />
                      )}
                      {phoneValidity === "invalid" && (
                        <WarningCircle
                          size={20}
                          weight="fill"
                          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-rose-500"
                          data-testid="phone-invalid-icon"
                        />
                      )}
                    </div>
                  </div>
                  {phoneValidity === "invalid" && (
                    <p className="mt-1.5 text-[12px] text-rose-600" data-testid="phone-invalid-hint">
                      {t.contact.form.phone_invalid || "Número de telefone inválido para o país selecionado."}
                    </p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {t.contact.form.subject}
                  </label>
                  <div className="relative">
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      data-testid="contact-input-subject"
                      className={`${inputBase} appearance-none pr-10`}
                    >
                      <option value="">{t.contact.form.subject_ph}</option>
                      {Object.entries(t.contact.form.subject_options).map(([k, label]) => (
                        <option key={k} value={label}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <CaretDown
                      size={14}
                      weight="bold"
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="mb-2 block font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-slate-700">
                    {t.contact.form.message} *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    placeholder={t.contact.form.message_ph}
                    rows={5}
                    className={`${inputBase} resize-none`}
                    data-testid="contact-input-message"
                  />
                  {form.subject === t.contact.form.subject_options.job && (
                    <div
                      data-testid="contact-job-notice"
                      className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-blue-200 bg-blue-50/70 px-4 py-3 text-sm text-blue-900"
                    >
                      <span>{t.contact.form.job_notice}</span>
                      <a
                        href="/carreiras"
                        data-testid="contact-job-link"
                        className="font-semibold text-blue-700 underline-offset-2 hover:text-blue-900 hover:underline"
                      >
                        {t.contact.form.job_link}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="group mt-6 inline-flex items-center gap-2 rounded-full bg-blue-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:bg-blue-500 hover:shadow-blue-600/40 active:scale-95 disabled:opacity-60"
              >
                {submitting ? t.contact.form.sending : t.contact.form.submit}
                <ArrowRight
                  size={16}
                  weight="bold"
                  className="transition-transform group-hover:translate-x-1"
                />
              </button>
            </form>
            <div
              data-testid="contact-map"
              className="mt-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
            >
              <iframe
                title="TVCabSat — Luanda"
                src={MAP_EMBED}
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>

        {/* Dopamine-style closing footer block — joyful CTA + quick links */}
        <div
          data-testid="contact-dopamine-block"
          className="contact-dopamine relative mt-16 overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-[#d8e8f5] via-[#f4f9ff] to-[#eaf4ff] shadow-xl shadow-blue-600/10"
        >
          <div
            className="contact-dopamine__bar absolute inset-x-0 top-0 h-1"
            style={{ borderTop: "4px solid transparent" }}
            aria-hidden
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-amber-300/20 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-sky-400/20 blur-3xl"
          />

          <div className="relative grid grid-cols-1 gap-10 p-8 md:grid-cols-12 md:p-12">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                <Sparkle size={12} weight="fill" />
                {t.contact.dopamine_quick_links}
              </div>
              <h2
                data-testid="contact-dopamine-title"
                className="font-heading mt-5 text-3xl font-black leading-tight tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
              >
                {t.contact.dopamine_title_pre}{" "}
                {t.contact.dopamine_title_post}
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base">
                {t.contact.dopamine_subtitle}
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="tel:+244925687036"
                  data-testid="contact-dopamine-call"
                  className="contact-dopamine__cta"
                >
                  <Phone size={16} weight="bold" />
                  {t.contact.dopamine_call_label}
                </a>
                <a
                  href="mailto:comercial@tvcabsat.ao"
                  data-testid="contact-dopamine-email"
                  className="contact-dopamine__cta contact-dopamine__cta--secondary"
                >
                  <EnvelopeSimple size={16} weight="bold" />
                  {t.contact.dopamine_email_label}
                </a>
                <a
                  href="https://wa.me/244925687036"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-dopamine-whatsapp"
                  aria-label={t.contact.dopamine_whatsapp_label}
                  className="contact-dopamine__icon"
                >
                  <WhatsappLogo size={20} weight="fill" />
                </a>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                {t.contact.dopamine_quick_links}
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5">
                {[
                  { href: "/#about", label: t.nav.about },
                  { href: "/equipamentos", label: t.nav.equipment },
                  { href: "/portfolio", label: t.nav.portfolio },
                  { href: "/#services", label: t.nav.services },
                  { href: "/#partners", label: t.nav.partners },
                  { href: "/carreiras", label: t.nav.careers },
                ].map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      data-testid={`dopamine-link-${l.label}`}
                      className="contact-dopamine__link text-sm"
                    >
                      <ArrowRight size={11} weight="bold" />
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
